import { motion } from "framer-motion";
import { Brain, Users, Sparkles, Shield, HeartHandshake } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export const BenefitsSection = () => {
  const benefits = [
    { 
      icon: Brain, 
      title: "Plans de Classe Intelligents", 
      description: "Prise en compte automatique des besoins spécifiques : PAP, TSA, troubles de la vue et de l'audition... L'algorithme place chaque élève de manière optimale." 
    },
    { 
      icon: Users, 
      title: "Co-construction avec les Élèves", 
      description: "Vos élèves participent à la création des plans avec leurs professeurs. Ils se sentent écoutés, les conflits de placement disparaissent." 
    },
    { 
      icon: Sparkles, 
      title: "Simple et Intuitif", 
      description: "Prise en main immédiate pour la direction, les professeurs et les élèves. Aucune formation technique requise." 
    },
  ];

  return (
    <section id="features" className="py-24" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#29282B] mb-4">
            Pourquoi choisir EduPlan ?
          </h2>
          <p className="text-[#29282B]/60 text-lg max-w-2xl mx-auto">
            Plus qu'un logiciel, une solution complète pour optimiser le temps de cours
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8 mb-8">
          {benefits.map((benefit, index) => (
            <motion.div key={index} variants={fadeUp} className="bg-white border border-[#D9DADC] rounded-xl p-8 card-hover" data-testid={`benefit-card-${index}`}>
              <div className="w-14 h-14 rounded-xl bg-[#FDF6E9] flex items-center justify-center mb-6">
                <benefit.icon className="text-[#E7A541]" size={28} />
              </div>
              <div className="w-10 h-1 bg-[#E7A541] mb-4" />
              <h3 className="font-display text-xl font-semibold text-[#29282B] mb-3">{benefit.title}</h3>
              <p className="text-[#29282B]/70">{benefit.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* RGPD Card */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="bg-[#FDF6E9] rounded-xl p-8 flex flex-col md:flex-row items-center gap-6 mb-8" data-testid="rgpd-card">
          <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
            <Shield className="text-[#E7A541]" size={32} />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-xl font-semibold text-[#29282B] mb-2">
              Sécurisé & Conforme RGPD
            </h3>
            <p className="text-[#29282B]/70">
              Vos données sont protégées et hébergées en Europe. Nous respectons les normes les plus strictes en matière de protection des données.
            </p>
          </div>
          <a 
            href="https://rgpd.eduplan-lnc.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-6 py-3 border-2 border-[#E7A541] text-[#E7A541] rounded-full font-semibold hover:bg-[#E7A541] hover:text-white transition-all duration-300 whitespace-nowrap"
          >
            En savoir plus
          </a>
        </motion.div>

        {/* Accompagnement 360° Card */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="bg-[#29282B] rounded-xl p-8 flex flex-col md:flex-row items-center gap-6" data-testid="accompagnement-card">
          <div className="w-16 h-16 rounded-xl bg-[#E7A541] flex items-center justify-center flex-shrink-0">
            <HeartHandshake className="text-white" size={32} />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-xl font-semibold text-white mb-2">
              Accompagnement 360°
            </h3>
            <p className="text-white/70">
              On scanne votre établissement, on configure toutes vos salles, on forme vos professeurs et vos délégués. 
              <strong className="text-[#E7A541]"> Sans surcoût.</strong>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BenefitsSection;
